import { z } from 'zod'
import * as Sentry from '@sentry/react'
import type { DesignApi } from '../../infrastructure/api/designApi'
import { errorMessages } from '../shared/errorMessages'

/**
 * Create Design Use Case
 * Handles the creation of new designs with proper validation and error handling
 */

// Command schema for creating a design
export const CreateDesignCommandSchema = z.object({
  cartId: z.string().min(1),
  productId: z.string().min(1),
  variationId: z.string().optional(),
  templateName: z.string().optional(),
  language: z.enum(['nl', 'fr', 'en', 'de']).optional(),
})

export type CreateDesignCommand = z.infer<typeof CreateDesignCommandSchema>

// Result type
export type CreateDesignResult = {
  success: boolean
  design?: {
    designId: string
    productId: number
    cartId: string
    background: string
    baseColor?: string
    type?: string
    zones: {
      background: { width: number }
      export: { top: number; left: number; width: number; height: number }
      design: { top: number; left: number; width: number; height: number }
    }
  }
  error?: string
}

// Factory function to create the use case
export const createCreateDesignUseCase = (designApi: DesignApi) => ({
  execute: async (command: CreateDesignCommand): Promise<CreateDesignResult> => {
    try {
      // Validate command
      const validatedCommand = CreateDesignCommandSchema.parse(command)

      // Always create new design via API
      const design = await designApi.createDesign({
        cartId: validatedCommand.cartId,
        productId: parseInt(validatedCommand.productId, 10),
        variationId: validatedCommand.variationId
          ? parseInt(validatedCommand.variationId, 10)
          : undefined,
        templateName: validatedCommand.templateName || 'test',
        language: validatedCommand.language || 'nl',
      })

      // TODO: Emit domain event
      // eventBus.emit(designEvents.designCreated(design))

      return {
        success: true,
        design,
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'createDesign' },
        extra: {
          cartId: command.cartId,
          productId: command.productId,
          variationId: command.variationId,
        },
      })
      return {
        success: false,
        error: error instanceof Error ? error.message : errorMessages.failedToCreateDesign(),
      }
    }
  },
})
